import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropertyCard from '../components/Properties/PropertyCard';
import FilterTabs from '../components/Properties/FilterTabs';
import LoginBanner from '../components/UI/LoginBanner';
import { Property } from '../types';
import { propertyService } from '../services/propertyService';
import { useUser } from '../contexts/UserContext';

const PropertiesPage: React.FC = () => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState('All');
  const navigate = useNavigate();
  const { isAuthenticated } = useUser();

  // Filter options matching the mockup
  const filterOptions = [
    'All',
    'Single Family',
    'Multi Family', 
    'Commercial',
    'Fund'
  ];

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    const applyFilter = () => {
      if (activeFilter === 'All') {
        setFilteredProperties(properties);
      } else {
        const filtered = properties.filter(property => {
          // Map filter categories to property types
          switch (activeFilter) {
            case 'Single Family':
              return property.property_type === 'Single Family' || property.property_type === 'SFR';
            case 'Multi Family':
              return property.property_type === 'Multi Family' || property.property_type === 'Multifamily';
            case 'Commercial':
              return property.property_type === 'Commercial';
            case 'Fund':
              return property.property_type === 'Fund';
            default:
              return true;
          }
        });
        setFilteredProperties(filtered);
      }
    };

    applyFilter();
  }, [properties, activeFilter]);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const data = await propertyService.getProperties();
      setProperties(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load properties');
      console.error('Error fetching properties:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePropertyClick = (property: Property) => {
    navigate(`/properties/${property.id}`);
  };

  const handleInvestClick = (e: React.MouseEvent, property: Property) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    navigate(`/invest/${property.id}`);
  };

  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter);
  };

  // Mock data for demo - includes the Private Credit Fund from mockup
  const mockProperties: Property[] = [
    {
      id: '1',
      name: 'Private Credit Fund',
      title: 'Private Credit Fund',
      description: 'Diversified private credit fund with institutional-grade returns',
      address: 'Multiple Locations',
      city: 'Multiple',
      state: 'US',
      zip_code: '00000',
      property_type: 'Fund',
      total_value: 100000000,
      total_shares: 1000000,
      available_shares: 250000,
      price_per_share: 100,
      annual_yield: 0.18,
      status: 'active',
      images: ['/api/placeholder/400/300'],
      documents: [],
      verification_status: 'verified',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      shares_sold: 750000,
      completion_percentage: 75,
      market_cap: 100000000,
      stakersCount: 18000
    },
    {
      id: '2', 
      name: '123 Main Street',
      title: '123 Main Street',
      description: 'Beautiful single-family home in Austin suburbs',
      address: '123 Main Street',
      city: 'Austin',
      state: 'TX',
      zip_code: '78701',
      property_type: 'Single Family',
      total_value: 5000000,
      total_shares: 100000,
      available_shares: 25000,
      price_per_share: 50,
      annual_yield: 0.12,
      status: 'active',
      images: ['/api/placeholder/400/300'],
      documents: [],
      verification_status: 'verified',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      shares_sold: 75000,
      completion_percentage: 75,
      market_cap: 5000000,
      stakersCount: 1216
    },
    {
      id: '3',
      name: 'Sunset Apartments',
      title: 'Sunset Apartments',
      description: 'Modern apartment complex with mountain views',
      address: '456 Sunset Boulevard',
      city: 'Phoenix',
      state: 'AZ',
      zip_code: '85001',
      property_type: 'Multi Family', 
      total_value: 15000000,
      total_shares: 200000,
      available_shares: 50000,
      price_per_share: 75,
      annual_yield: 0.14,
      status: 'active',
      images: ['/api/placeholder/400/300'],
      documents: [],
      verification_status: 'verified',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      shares_sold: 150000,
      completion_percentage: 75,
      market_cap: 15000000,
      stakersCount: 892
    }
  ];

  // Use mock data if no real data is available
  const displayProperties = properties.length > 0 ? filteredProperties : mockProperties.filter(prop => {
    if (activeFilter === 'All') return true;
    return prop.property_type === activeFilter || 
           (activeFilter === 'Single Family' && prop.property_type === 'Single Family') ||
           (activeFilter === 'Multi Family' && prop.property_type === 'Multi Family') ||
           (activeFilter === 'Fund' && prop.property_type === 'Fund');
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading properties...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchProperties}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Login Banner - shown when not authenticated like in mockup */}
      {!isAuthenticated && <LoginBanner />}
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Investment Properties
          </h1>
          <p className="text-gray-600">
            Discover curated investment opportunities
          </p>
        </div>

        {/* Filter Tabs - matching mockup design */}
        <div className="mb-8">
          <FilterTabs
            filters={filterOptions}
            activeFilter={activeFilter}
            onFilterChange={handleFilterChange}
          />
        </div>

        {/* Properties Grid */}
        {displayProperties.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No properties found</h3>
            <p className="text-gray-600">
              {activeFilter === 'All' 
                ? 'No properties are currently available.' 
                : `No ${activeFilter.toLowerCase()} properties are currently available.`
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayProperties.map((property) => (
              <PropertyCard
                key={property.id}
                property={property}
                onClick={() => handlePropertyClick(property)}
                onInvestClick={(e) => handleInvestClick(e, property)}
                showInvestButton={isAuthenticated}
              />
            ))}
          </div>
        )}

        {/* Load More Button - if needed for pagination */}
        {displayProperties.length > 0 && displayProperties.length % 9 === 0 && (
          <div className="text-center mt-8">
            <button className="px-6 py-3 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
              Load More Properties
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertiesPage;
